import type { UndefinedOnPartialDeep } from 'type-fest';

import type { SelectedServices } from '@repo/api-adapters/selectors/services.selectors';
import {
    buildAlignProposalPayload,
    buildAlignProposalQueryParams,
} from '@repo/api-adapters/services/align-proposal.utils';
import { buildCompositeServiceId } from '@repo/api-adapters/services/services.api.utils';
import {
    type AlignProposalPayload,
    type AlignProposalResponse,
    type AlignProposalServiceSuccess,
    type AlignProposalTimeWindow,
    isAlignProposalServiceSuccess,
} from '@repo/types/align-proposal';
import type { OrderAsset } from '@repo/types/order';
import type { Service } from '@repo/types/service';
import type { TimeWindow } from '@repo/types/time-windows';
import { computeDayKey, formatDateTime, formatTime } from '@repo/utils';

interface AlignProposalProvidedServiceCandidate {
    serviceId: string;
    serviceProviderId: string;
}

interface AffectedServiceUpdateItem {
    key: string;
    name: string;
    proposedTimeWindowLabel: string;
}

interface DisplayedTimeWindowAlignProposalRequest {
    timeWindowId: string;
    payload: AlignProposalPayload;
    queryParams: NonNullable<ReturnType<typeof buildAlignProposalQueryParams>>;
}

interface DisplayedTimeWindowAlignProposalRequestsResult {
    blockedTimeWindowIds: ReadonlySet<string>;
    requests: DisplayedTimeWindowAlignProposalRequest[];
}

const getAlignProposalProvidedServiceCandidates = ({
    orderData,
    requestedServiceLines,
}: {
    orderData: OrderAsset;
    requestedServiceLines: AlignProposalPayload['serviceLines'];
}): AlignProposalProvidedServiceCandidate[] => {
    const requestedServiceIds = new Set(requestedServiceLines.map((serviceLine) => serviceLine.serviceNo));
    const providedServices = orderData.assetDetails._embedded?.providedServices ?? [];

    return providedServices.flatMap((service) => {
        const serviceId = buildCompositeServiceId(service.serviceItemType, service.serviceItemNo);
        const serviceProviderId = service.serviceProviderId;

        if (!serviceId || !serviceProviderId || !requestedServiceIds.has(serviceId)) {
            return [];
        }

        return [
            {
                serviceId,
                serviceProviderId,
            },
        ];
    });
};

const findAlignProposalServiceId = ({
    alignedService,
    candidates,
}: {
    alignedService: AlignProposalServiceSuccess;
    candidates: AlignProposalProvidedServiceCandidate[];
}): string | undefined => {
    const validatedMatches = candidates.filter(
        (candidate) =>
            candidate.serviceId === alignedService.serviceNo &&
            candidate.serviceProviderId === alignedService.serviceProviderId
    );

    return validatedMatches.length === 1 ? validatedMatches[0]?.serviceId : undefined;
};

const formatProposedTimeWindow = ({
    timeWindow,
    dateTimeFormat,
    timeFormat,
}: {
    timeWindow: AlignProposalTimeWindow;
    dateTimeFormat: string;
    timeFormat: string;
}) => {
    const isSameDay =
        computeDayKey(timeWindow.fromDateTime, timeWindow.timeZone) ===
        computeDayKey(timeWindow.toDateTime, timeWindow.timeZone);

    if (isSameDay) {
        return `${formatDateTime(timeWindow.fromDateTime, dateTimeFormat, timeWindow.timeZone)} - ${formatTime(timeWindow.toDateTime, timeFormat, timeWindow.timeZone)}`;
    }

    return `${formatDateTime(timeWindow.fromDateTime, dateTimeFormat, timeWindow.timeZone)} - ${formatDateTime(timeWindow.toDateTime, dateTimeFormat, timeWindow.timeZone)}`;
};

export interface TimeWindowAlignProposalAffectedService {
    currentTimeWindow?: UndefinedOnPartialDeep<TimeWindow> | undefined;
    name?: string | undefined;
    proposedTimeWindow: AlignProposalTimeWindow;
    serviceId: string;
}

interface TimeWindowAlignProposalEvaluationResult {
    affectedServices: TimeWindowAlignProposalAffectedService[];
    errorCode?: string | undefined;
    isBlocking: boolean;
}

interface EvaluateTimeWindowAlignProposalParams {
    orderData: OrderAsset;
    services: SelectedServices;
    alignProposal: AlignProposalResponse;
    requestedServiceLines: AlignProposalPayload['serviceLines'];
}

export const buildTimeWindowAlignProposalRequest = ({
    orderData,
    services,
    serviceId,
    isDeliveryTrigger,
    timeWindow,
    queryParams = buildAlignProposalQueryParams(orderData),
}: {
    orderData: OrderAsset;
    services: SelectedServices;
    serviceId: string;
    isDeliveryTrigger: boolean;
    timeWindow: TimeWindow;
    queryParams?: ReturnType<typeof buildAlignProposalQueryParams>;
}) => ({
    payload: buildAlignProposalPayload({
        orderData,
        services,
        triggerServiceId: serviceId,
        isDeliveryTrigger,
        candidateTimeWindow: timeWindow,
    }),
    queryParams,
});

export const getAffectedServiceUpdateItems = ({
    affectedServices,
    dateTimeFormat,
    timeFormat,
}: {
    affectedServices: TimeWindowAlignProposalAffectedService[];
    dateTimeFormat: string;
    timeFormat: string;
}): AffectedServiceUpdateItem[] => {
    return affectedServices.flatMap((affectedService) => {
        if (!affectedService.name) {
            return [];
        }

        return [
            {
                key: `${affectedService.serviceId}:${affectedService.proposedTimeWindow.id}`,
                name: affectedService.name,
                proposedTimeWindowLabel: formatProposedTimeWindow({
                    timeWindow: affectedService.proposedTimeWindow,
                    dateTimeFormat,
                    timeFormat,
                }),
            },
        ];
    });
};

export const getDisplayedTimeWindowAlignProposalRequests = ({
    orderData,
    services,
    service,
    timeWindows,
}: {
    orderData: OrderAsset | undefined;
    services: SelectedServices;
    service: UndefinedOnPartialDeep<Service>;
    timeWindows: TimeWindow[];
}): DisplayedTimeWindowAlignProposalRequestsResult => {
    const serviceId = service.serviceId;
    const currentTimeWindowId = service.timeWindow?.timeWindowId;
    const blockedTimeWindowIds = new Set<string>();

    if (!orderData || !serviceId) {
        return { blockedTimeWindowIds, requests: [] };
    }

    const queryParams = buildAlignProposalQueryParams(orderData);
    const requests: DisplayedTimeWindowAlignProposalRequest[] = [];

    for (const timeWindow of timeWindows) {
        if (timeWindow.timeWindowId === currentTimeWindowId) {
            continue;
        }

        const { payload } = buildTimeWindowAlignProposalRequest({
            orderData,
            services,
            serviceId,
            isDeliveryTrigger: Boolean(service.isDelivery),
            timeWindow,
            queryParams,
        });

        if (!payload) {
            continue;
        }

        if (!queryParams) {
            blockedTimeWindowIds.add(timeWindow.timeWindowId);
            continue;
        }

        requests.push({
            timeWindowId: timeWindow.timeWindowId,
            payload,
            queryParams,
        });
    }

    return { blockedTimeWindowIds, requests };
};

export const evaluateTimeWindowAlignProposal = ({
    orderData,
    services,
    alignProposal,
    requestedServiceLines,
}: EvaluateTimeWindowAlignProposalParams): TimeWindowAlignProposalEvaluationResult => {
    const candidates = getAlignProposalProvidedServiceCandidates({ orderData, requestedServiceLines });
    const servicesById = new Map(services.map((service) => [service.serviceId, service]));
    const requestedServiceLinesByServiceNo = new Map(
        requestedServiceLines.map((serviceLine) => [serviceLine.serviceNo, serviceLine])
    );
    const triggerServiceLine = requestedServiceLines.find(
        (serviceLine) => serviceLine.manualChangeType === 'STARTTIME'
    );
    const matchedServiceIds = new Set<string>();
    const affectedServices: TimeWindowAlignProposalAffectedService[] = [];

    for (const alignedService of alignProposal.services) {
        const requestedServiceLine = requestedServiceLinesByServiceNo.get(alignedService.serviceNo);

        if (!requestedServiceLine) {
            return { isBlocking: true, affectedServices: [] };
        }

        if (!isAlignProposalServiceSuccess(alignedService)) {
            return { isBlocking: true, errorCode: alignedService.errorCode, affectedServices: [] };
        }

        if (requestedServiceLine.serviceProviderId !== alignedService.serviceProviderId) {
            return { isBlocking: true, affectedServices: [] };
        }

        const proposedTimeWindow = alignedService.timeWindows[0];

        if (!proposedTimeWindow) {
            return { isBlocking: true, affectedServices: [] };
        }

        if (
            triggerServiceLine?.serviceNo === alignedService.serviceNo &&
            proposedTimeWindow.id !== triggerServiceLine.timeWindowId
        ) {
            return { isBlocking: true, affectedServices: [] };
        }

        if (triggerServiceLine?.serviceNo === alignedService.serviceNo) {
            continue;
        }

        if (proposedTimeWindow.id === requestedServiceLine.timeWindowId) {
            continue;
        }

        const matchedServiceId = findAlignProposalServiceId({ alignedService, candidates });

        if (!matchedServiceId || matchedServiceIds.has(matchedServiceId)) {
            return { isBlocking: true, affectedServices: [] };
        }

        matchedServiceIds.add(matchedServiceId);

        const matchedService = servicesById.get(matchedServiceId);

        affectedServices.push({
            serviceId: matchedServiceId,
            ...(matchedService?.timeWindow && { currentTimeWindow: matchedService.timeWindow }),
            proposedTimeWindow,
            name: matchedService?.serviceName ?? alignedService.serviceProviderName ?? alignedService.serviceCode,
        });
    }

    return {
        isBlocking: false,
        affectedServices,
    };
};
